import React, { useEffect, useState, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { AuthContext } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProgramDetail = () => {
  const { id } = useParams();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [program, setProgram] = useState(null);
  const [school, setSchool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [processingBooking, setProcessingBooking] = useState(false);
  const [formData, setFormData] = useState({
    student_name: "",
    student_email: "",
    student_phone: "",
    message: ""
  });
  const [paymentType, setPaymentType] = useState("full"); // "full" or "deposit"
  const [showSchedule, setShowSchedule] = useState(false);
  const [branding, setBranding] = useState({ logo_url: null, site_name: "Train In Japan" });

  // Calculate pricing with fees
  const calculatePricing = () => {
    if (!program) return { schoolPrice: 0, platformFee: 0, subtotal: 0, salesTax: 0, total: 0, deposit: 0 };
    
    const schoolPrice = program.price || 0;
    const platformFeePercent = program.platform_fee_percentage || 9;
    const salesTaxPercent = program.sales_tax_percentage || 10;
    
    const platformFee = Math.round(schoolPrice * (platformFeePercent / 100));
    const subtotal = schoolPrice + platformFee;
    const salesTax = Math.round(subtotal * (salesTaxPercent / 100));
    const total = subtotal + salesTax;
    const deposit = Math.round(total * 0.12); // 12% deposit
    
    return { schoolPrice, platformFee, subtotal, salesTax, total, deposit };
  };

  const pricing = calculatePricing();

  useEffect(() => {
    fetchProgramDetails();
    axios.get(`${API}/branding/logo`).then(res => setBranding(res.data)).catch(err => console.error("Branding error:", err));
    
    // Check if returning from auth with pending booking
    console.log("Checking for pending booking, user:", user ? user.email : "not logged in");
    const pendingBooking = localStorage.getItem("pendingBooking");
    console.log("Pending booking data:", pendingBooking);
    if (pendingBooking && user) {
      const bookingData = JSON.parse(pendingBooking);
      if (bookingData.courseId === id) {
        localStorage.removeItem("pendingBooking");
        setFormData(bookingData.formData);
        setPaymentType(bookingData.paymentType || "full");
        setTimeout(() => {
          processBookingAfterAuth(bookingData.formData, bookingData.paymentType || "full");
        }, 500);
      }
    }
  }, [id, user]);

  const fetchProgramDetails = async () => {
    try {
      const programRes = await axios.get(`${API}/programs/${id}`);
      setProgram(programRes.data);
      
      const schoolRes = await axios.get(`${API}/schools/${programRes.data.school_id}`);
      setSchool(schoolRes.data);
    } catch (error) {
      console.error("Failed to fetch program details:", error);
      toast.error("Failed to load program details");
    } finally {
      setLoading(false);
    }
  };

  const processBookingAfterAuth = async (bookingFormData, selectedPaymentType = "full") => {
    setProcessingBooking(true);
    setBookingOpen(false);
    toast.info("Processing your booking...");
    
    try {
      // Step 1: Create booking
      await axios.post(
        `${API}/programs/${id}/bookings`,
        bookingFormData,
        { withCredentials: true }
      );
      
      // Step 2: Initiate payment with selected payment type
      const originUrl = window.location.origin;
      const paymentResponse = await axios.post(
        `${API}/payments/checkout`,
        { course_id: id, origin_url: originUrl, payment_type: selectedPaymentType },
        { withCredentials: true }
      );
      
      // Step 3: Redirect to Stripe checkout
      if (paymentResponse.data.checkout_url) {
        toast.success("Redirecting to payment...");
        window.location.href = paymentResponse.data.checkout_url;
      } else {
        throw new Error("No checkout URL received");
      }
    } catch (error) {
      console.error("Booking/Payment failed:", error);
      const errorMsg = typeof error.response?.data?.detail === "string" 
        ? error.response.data.detail 
        : error.response?.data?.message || error.message || "Failed to process booking";
      toast.error(errorMsg);
      setProcessingBooking(false);
    }
  };

  const handleBooking = async (e) => {
    e.preventDefault();
    
    if (!formData.student_name || !formData.student_email) {
      toast.error("Please fill in your name and email");
      return;
    }
    
    if (!user) {
      // Save booking data including payment type to localStorage
      localStorage.setItem("pendingBooking", JSON.stringify({
        courseId: id,
        formData: formData,
        paymentType: paymentType
      }));
      
      toast.info("Please sign in to complete your booking");
      const redirectUrl = `${window.location.origin}/programs/${id}`;
      window.location.href = `${API}/auth/google/login?redirect_to=${encodeURIComponent(redirectUrl)}`;
      return;
    }

    // User is already logged in, proceed directly
    await processBookingAfterAuth(formData, paymentType);
  };

  if (loading || processingBooking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-emerald-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-700"></div>
          <p className="mt-4 text-slate-600">{processingBooking ? "Processing your booking..." : "Loading..."}</p>
        </div>
      </div>
    );
  }

  if (!program) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-emerald-50">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Program not found</h2>
          <Button onClick={() => navigate("/programs")}>Browse Programs</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen overflow-x-hidden bg-gradient-to-br from-slate-50 to-emerald-50">
      {/* Floating Logo - Hidden on mobile to avoid overlap */}
      <div className="hidden md:fixed md:block top-3 left-6 z-[60] cursor-pointer" onClick={() => navigate("/")}>
        <div className="flex flex-col items-center" style={{ filter: "drop-shadow(0 4px 12px rgba(0,0,0,0.25))" }}>
          {branding.logo_url && (
            <div className="w-20 h-20 rounded-full bg-white border-[3px] border-emerald-600 overflow-hidden">
              <img src={branding.logo_url} alt="Logo" className="w-full h-full object-cover" />
            </div>
          )}
          <span className="mt-1 text-base font-bold text-slate-800 bg-white/90 px-2 py-0.5 rounded-full" style={{ fontFamily: "Playfair Display, serif" }}>{branding.site_name}</span>
        </div>
      </div>

      {/* Navigation - Mobile optimized */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-lg border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-2.5 sm:py-3 flex items-center justify-between">
          {/* Mobile: Show clickable site name */}
          <div className="md:hidden flex items-center gap-2 cursor-pointer" onClick={() => navigate("/")}>
            {branding.logo_url && (
              <div className="w-8 h-8 rounded-full bg-white border-2 border-emerald-600 overflow-hidden flex-shrink-0">
                <img src={branding.logo_url} alt="Logo" className="w-full h-full object-cover" />
              </div>
            )}
            <span className="text-sm font-semibold text-slate-800 truncate" style={{ fontFamily: "Playfair Display, serif" }}>{branding.site_name}</span>
          </div>
          {/* Desktop: Spacer for floating logo */}
          <div className="hidden md:block w-12"></div>
          <div className="flex items-center gap-3 sm:gap-6">
            <button onClick={() => navigate("/programs")} className="text-sm sm:text-base text-slate-700 hover:text-emerald-800 font-medium transition-colors">Programs</button>
            {user && (
              <Button onClick={() => navigate("/dashboard")} variant="default" size="sm" className="bg-emerald-700 hover:bg-emerald-800 text-sm">Dashboard</Button>
            )}
          </div>
        </div>
      </nav>

      {/* Program Details - Mobile optimized */}
      <section data-testid="program-detail-section" className="pt-16 sm:pt-20 md:pt-24 pb-8 sm:pb-12 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12">
            {/* Image */}
            <div>
              {program.image_url ? (
                <img 
                  src={program.image_url} 
                  alt={program.title} 
                  className="w-full h-64 sm:h-80 lg:h-96 object-cover rounded-xl sm:rounded-2xl shadow-xl"
                />
              ) : (
                <div className="w-full h-96 bg-gradient-to-br from-slate-200 to-slate-300 rounded-2xl shadow-xl flex items-center justify-center">
                  <span className="text-slate-500">No Image Available</span>
                </div>
              )}
            </div>

            {/* Info */}
            <div>
              <div className="mb-3 sm:mb-4">
                <span className="text-xs font-semibold text-emerald-700 bg-emerald-100 px-2 sm:px-3 py-1 rounded-full">{program.category}</span>
              </div>
              
              {/* School Branding */}
              {school && (
                <Card className="mb-4 sm:mb-6 bg-gradient-to-r from-slate-50 to-emerald-50">
                  <CardContent className="p-3 sm:p-4">
                    <div className="flex items-center gap-3 sm:gap-4">
                      {school.logo_url ? (
                        <img 
                          src={school.logo_url} 
                          alt={school.name}
                          className="w-12 h-12 sm:w-16 sm:h-16 rounded-full object-cover border-2 border-emerald-200 shadow-md flex-shrink-0"
                        />
                      ) : (
                        <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-emerald-200 flex items-center justify-center shadow-md flex-shrink-0">
                          <span className="text-emerald-900 font-bold text-xl sm:text-2xl">
                            {school.name.charAt(0)}
                          </span>
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-xs sm:text-sm text-slate-500 uppercase tracking-wide">Offered by</p>
                        <h3 className="text-base sm:text-xl font-bold text-slate-900 truncate" style={{ fontFamily: "Playfair Display, serif" }}>
                          {school.name}
                        </h3>
                        {school.location && (
                          <p className="text-xs sm:text-sm text-slate-600 mt-1 truncate">📍 {school.location}</p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
              
              <h1 data-testid="program-title" className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 mb-3 sm:mb-4" style={{ fontFamily: "Playfair Display, serif" }}>{program.title}</h1>
              <p data-testid="program-description" className="text-sm sm:text-base lg:text-lg text-slate-600 mb-4 sm:mb-6 leading-relaxed">{program.description}</p>

              {/* Program Info Card */}
              <Card className="mb-4 sm:mb-6">
                <CardContent className="p-4 sm:p-6 space-y-2 sm:space-y-3">
                  <div className="flex justify-between text-sm sm:text-base">
                    <span className="text-slate-600 font-medium">Duration:</span>
                    <span data-testid="program-duration" className="text-slate-900 font-semibold">{program.duration}</span>
                  </div>
                  {program.start_date && (
                    <div className="flex justify-between text-sm sm:text-base">
                      <span className="text-slate-600 font-medium">Start Date:</span>
                      <span data-testid="program-start-date" className="text-slate-900 font-semibold">{new Date(program.start_date).toLocaleDateString()}</span>
                    </div>
                  )}
                  {program.end_date && (
                    <div className="flex justify-between">
                      <span className="text-slate-600 font-medium">End Date:</span>
                      <span className="text-slate-900 font-semibold">{new Date(program.end_date).toLocaleDateString()}</span>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Pricing Breakdown Card */}
              <Card className="mb-6 border-2 border-emerald-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">Pricing Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Program Fee ({school?.name || "School"}):</span>
                    <span className="text-slate-900">¥{pricing.schoolPrice?.toLocaleString("ja-JP")}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Train In Japan Fee (9%):</span>
                    <span className="text-slate-900">¥{pricing.platformFee?.toLocaleString("ja-JP")}</span>
                  </div>
                  <div className="flex justify-between text-sm border-t pt-2">
                    <span className="text-slate-600">Subtotal:</span>
                    <span className="text-slate-900">¥{pricing.subtotal?.toLocaleString("ja-JP")}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Sales Tax (10%):</span>
                    <span className="text-slate-900">¥{pricing.salesTax?.toLocaleString("ja-JP")}</span>
                  </div>
                  <div className="flex justify-between font-bold text-lg border-t pt-2 mt-2">
                    <span className="text-slate-900">Total:</span>
                    <span className="text-emerald-700">¥{pricing.total?.toLocaleString("ja-JP")}</span>
                  </div>
                  <div className="flex justify-between text-sm text-emerald-600 bg-emerald-50 p-2 rounded mt-2">
                    <span>Or pay 12% deposit now:</span>
                    <span className="font-semibold">¥{pricing.deposit?.toLocaleString("ja-JP")}</span>
                  </div>
                </CardContent>
              </Card>

              {/* View Schedule Button */}
              {program.start_date && program.end_date && (
                <Card className="mb-6">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle>Training Schedule</CardTitle>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setShowSchedule(!showSchedule)}
                      >
                        {showSchedule ? "Hide Schedule" : "View Schedule"}
                      </Button>
                    </div>
                  </CardHeader>
                  {showSchedule && (
                    <CardContent>
                      <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                        <div className="flex items-center gap-2 text-sm">
                          <span className="font-medium text-slate-700">Program Duration:</span>
                          <span className="text-slate-900">
                            {new Date(program.start_date).toLocaleDateString()} - {new Date(program.end_date).toLocaleDateString()}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <span className="font-medium text-slate-700">Daily Training Hours:</span>
                          <span className="text-slate-900">
                            {program.daily_start_time || "09:00"} - {program.daily_end_time || "17:00"}
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  )}
                </Card>
              )}

              <Dialog open={bookingOpen} onOpenChange={setBookingOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="book-now-btn" size="lg" className="w-full bg-emerald-700 hover:bg-emerald-800 text-lg py-6">
                    Book Now
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Book Program</DialogTitle>
                    <DialogDescription>
                      Fill in your details to proceed to payment
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleBooking} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Full Name *</label>
                      <input 
                        data-testid="booking-name-input"
                        type="text" 
                        required
                        value={formData.student_name}
                        onChange={(e) => setFormData({...formData, student_name: e.target.value})}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        placeholder="Enter your full name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Email *</label>
                      <input 
                        data-testid="booking-email-input"
                        type="email" 
                        required
                        value={formData.student_email}
                        onChange={(e) => setFormData({...formData, student_email: e.target.value})}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        placeholder="Enter your email"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
                      <input 
                        data-testid="booking-phone-input"
                        type="tel" 
                        value={formData.student_phone}
                        onChange={(e) => setFormData({...formData, student_phone: e.target.value})}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        placeholder="Enter your phone number"
                      />
                    </div>
                    
                    {/* Payment Type Selection */}
                    <div className="space-y-3">
                      <label className="block text-sm font-medium text-slate-700">Payment Option</label>
                      <div className="space-y-2">
                        <label className={`flex items-center p-3 border rounded-lg cursor-pointer transition-all ${paymentType === "full" ? "border-emerald-500 bg-emerald-50" : "border-slate-200 hover:border-slate-300"}`}>
                          <input
                            type="radio"
                            name="paymentType"
                            value="full"
                            checked={paymentType === "full"}
                            onChange={() => setPaymentType("full")}
                            className="mr-3 text-emerald-600"
                          />
                          <div className="flex-1">
                            <div className="font-medium text-slate-900">Pay in Full</div>
                            <div className="text-sm text-slate-500">¥{pricing.total?.toLocaleString("ja-JP")}</div>
                          </div>
                        </label>
                        <label className={`flex items-center p-3 border rounded-lg cursor-pointer transition-all ${paymentType === "deposit" ? "border-emerald-500 bg-emerald-50" : "border-slate-200 hover:border-slate-300"}`}>
                          <input
                            type="radio"
                            name="paymentType"
                            value="deposit"
                            checked={paymentType === "deposit"}
                            onChange={() => setPaymentType("deposit")}
                            className="mr-3 text-emerald-600"
                          />
                          <div className="flex-1">
                            <div className="font-medium text-slate-900">Pay 12% Deposit</div>
                            <div className="text-sm text-slate-500">¥{pricing.deposit?.toLocaleString("ja-JP")} now, ¥{(pricing.total - pricing.deposit)?.toLocaleString("ja-JP")} remaining</div>
                          </div>
                        </label>
                      </div>
                    </div>
                    
                    {/* Price Summary */}
                    <div className="bg-slate-50 p-4 rounded-lg space-y-2">
                      <div className="text-sm text-slate-600">
                        <div className="flex justify-between">
                          <span>Program Fee:</span>
                          <span>¥{pricing.schoolPrice?.toLocaleString("ja-JP")}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Train In Japan Fee (9%):</span>
                          <span>¥{pricing.platformFee?.toLocaleString("ja-JP")}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Sales Tax (10%):</span>
                          <span>¥{pricing.salesTax?.toLocaleString("ja-JP")}</span>
                        </div>
                      </div>
                      <div className="flex justify-between items-center border-t pt-2">
                        <span className="font-medium text-slate-700">
                          {paymentType === "deposit" ? "Pay Now (12% Deposit):" : "Total:"}
                        </span>
                        <span className="text-xl font-bold text-emerald-700">
                          ¥{(paymentType === "deposit" ? pricing.deposit : pricing.total)?.toLocaleString("ja-JP")}
                        </span>
                      </div>
                      {paymentType === "deposit" && (
                        <div className="text-sm text-amber-600 bg-amber-50 p-2 rounded">
                          Remaining balance of ¥{(pricing.total - pricing.deposit)?.toLocaleString("ja-JP")} due before program starts
                        </div>
                      )}
                    </div>
                    
                    {!user && (
                      <p className="text-sm text-emerald-600 text-center">You will sign in with Google to complete booking</p>
                    )}
                    
                    <Button data-testid="booking-submit-btn" type="submit" className="w-full bg-emerald-700 hover:bg-emerald-800">
                      {user ? (paymentType === "deposit" ? "Pay Deposit Now" : "Pay in Full") : "Sign In & Pay"}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* School Info */}
          {school && (
            <Card className="mt-12">
              <CardHeader>
                <CardTitle style={{ fontFamily: "Spectral, serif" }}>About the School</CardTitle>
              </CardHeader>
              <CardContent>
                <h3 className="text-xl font-bold text-slate-900 mb-2">{school.name}</h3>
                <p className="text-slate-600 mb-4">{school.description}</p>
                <div className="space-y-2 text-sm">
                  <p className="text-slate-600"><strong>Location:</strong> {school.location}</p>
                  <p className="text-slate-600"><strong>Contact:</strong> {school.contact_email}</p>
                  {school.website && <p className="text-slate-600"><strong>Website:</strong> <a href={school.website} target="_blank" rel="noopener noreferrer" className="text-emerald-700 hover:underline">{school.website}</a></p>}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </section>
    </div>
  );
};

export default ProgramDetail;
